// Portfolio Administration v1.0 PR 7 — Row 2 (Performance).

import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import type { PortfolioPerformanceMetrics } from "@/api/portfolioAdmin/pr7Types";
import { chartTheme } from "@/pages/dealFlow/metrics/chartTheme";
import {
  MetricCard,
  MetricCardWithChart,
  MetricsCardRow,
  formatMoney,
  formatPercent,
} from "./MetricsCards";

/**
 * 🔴 Fills resolve through `chartTheme` (which reads `tokens.json`), never hex
 * literals — a literal is a second palette that cannot be found when a token
 * moves. Each series is named by what it MEANS, and the two adverse series on
 * this row deliberately take different tones: per the Table & Tile Standard's
 * badge vocabulary, red is "overdue" and amber is "attention". Delinquency
 * buckets are the overdue population; remediation levels are a breakdown of
 * cases already being worked. Same tone for both would have made the two charts
 * read as one series.
 */
const DELINQUENCY_FILL = chartTheme.error;
const REMEDIATION_FILL = chartTheme.warning;

/**
 * DSCR distribution is a HISTOGRAM of where loans fall, not a verdict on them —
 * a bar in the lowest bucket is not a success and the top bucket is not a
 * failure. `info` is the neutral tone that says "here is the shape of the book"
 * without colouring an opinion onto it.
 */
const DSCR_FILL = chartTheme.info;

export function PerformanceRow({ data }: { data: PortfolioPerformanceMetrics }) {
  const d = data.delinquency;
  const delinquencyBuckets = [
    { bucket: "30+", count: d.bucket30Plus },
    { bucket: "60+", count: d.bucket60Plus },
    { bucket: "90+", count: d.bucket90Plus },
  ];

  const defaultRows = Object.entries(data.defaultCountByRemediationLevel).map(
    ([level, count]) => ({ level, count }),
  );

  return (
    <MetricsCardRow title="Performance" testId="metrics-row-performance">
      <MetricCardWithChart
        label="Delinquency"
        value={formatPercent(d.delinquencyRate)}
        caption={`30+: ${d.bucket30Plus} · 60+: ${d.bucket60Plus} · 90+: ${d.bucket90Plus}`}
        testId="card-delinquency"
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={delinquencyBuckets}>
            <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
            <YAxis hide />
            <Bar dataKey="count" fill={DELINQUENCY_FILL} />
            <Tooltip />
          </BarChart>
        </ResponsiveContainer>
      </MetricCardWithChart>

      <MetricCardWithChart
        label="DSCR distribution"
        value={`${data.dscrDistribution.length} buckets`}
        caption="Current-balance vs original (proxy)"
        testId="card-dscr"
      >
        {data.dscrDistribution.length === 0 ? (
          <p className="text-small text-gray-mid">No loans on file.</p>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.dscrDistribution}>
              <XAxis dataKey="rangeLabel" tick={{ fontSize: 10 }} />
              <YAxis hide />
              <Bar dataKey="loanCount" fill={DSCR_FILL} />
              <Tooltip />
            </BarChart>
          </ResponsiveContainer>
        )}
      </MetricCardWithChart>

      <MetricCard
        label="Covenant test pass rate"
        value={formatPercent(data.covenantPassRateRolling)}
        caption={`Last Q: ${formatPercent(data.covenantPassRateLastQuarter)}`}
        testId="card-covenant"
      />

      <MetricCard
        label="Seasoning gate pass rate"
        value={formatPercent(data.seasoningGatePassRate)}
        caption="Rolling 12 months"
        testId="card-seasoning"
      />

      <MetricCardWithChart
        label="Defaults by remediation level"
        value={Object.values(data.defaultCountByRemediationLevel)
          .reduce((a, b) => a + b, 0)
          .toString()}
        caption="A / B / C / D breakdown"
        testId="card-defaults-by-level"
      >
        {defaultRows.length === 0 ? (
          <p className="text-small text-gray-mid">No open default cases.</p>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={defaultRows}>
              <XAxis dataKey="level" tick={{ fontSize: 11 }} />
              <YAxis hide />
              <Bar dataKey="count" fill={REMEDIATION_FILL} />
              <Tooltip />
            </BarChart>
          </ResponsiveContainer>
        )}
      </MetricCardWithChart>

      <MetricCard
        label="Total workout exposure"
        value={formatMoney(data.totalWorkoutExposure)}
        caption="Sum of original exposure on open defaults"
        testId="card-workout-exposure"
      />

      <MetricCard
        label="Recovery ratio"
        value={formatPercent(data.recoveryRatio)}
        caption="Recoveries ÷ exposure"
        testId="card-recovery-ratio"
      />

      <MetricCard
        label="Loss rate"
        value={formatPercent(data.lossRate)}
        caption="Charge-offs ÷ portfolio balance"
        testId="card-loss-rate"
      />
    </MetricsCardRow>
  );
}
